// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { post } from '@tls/auth';
import { CreateContainerProgressMessage } from '@tls/sw91-communication/types/com.api_tai_exporter';
import { HubContext } from 'components/common/emitter/HubContext';
import { ProjectVersion } from 'model/ProjectMetaMessageExtensions';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useDownloadFileApi from './useDownloadApi';
import useAppPermissions from './useAppPermissions';
import useIsQC from './useIsQC';

export function useModelExportApi(projectId: string) {
  const [loading, setLoading] = useState(false);
  const [lastStatus, setLastStatus] = useState<number | undefined>(200);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const { hasExportVliTaiPermission } = useAppPermissions();
  const isQC = useIsQC(projectId);

  const resolve = useRef<((success: boolean) => void) | undefined>(undefined);
  const reject = useRef<((success: boolean) => void) | undefined>(undefined);
  const [exportingModel, setExportingModel] = useState<string>();

  const { events } = useContext(HubContext);

  // Listen to events as soon as the exportingModel was set
  useEffect(() => {
    function onProgressUpdate(message: CreateContainerProgressMessage) {
      const progress = message.progress ?? 0;
      setExportProgress(progress);
      if (progress >= 1) {
        setLoading(false);
      }
    }
    function onProgressError() {
      if (!reject.current) return;
      reject.current(false);
    }

    events.on('onExportProgress', onProgressUpdate);
    events.on('onExportFailed', onProgressError);
    return () => {
      events.off('onExportProgress', onProgressUpdate);
      events.off('onExportFailed', onProgressError);
    };
  }, [events, exportingModel, reject]);

  // Resolve the getResult callback once the progress reaches 100%
  useEffect(() => {
    if (exportProgress < 1) return;
    if (!resolve.current) return;
    resolve.current(true);
  }, [exportProgress, resolve]);

  const getExportResult = useCallback(
    (): Promise<boolean> =>
      new Promise<boolean>((currentResolve, currentReject) => {
        resolve.current = currentResolve;
        reject.current = currentReject;
      }),
    []
  );

  const triggerExport = useCallback(
    async (modelId: ProjectVersion) => {
      setLoading(true);
      setExportProgress(0);
      setLastStatus(200);

      setExportingModel(modelId);
      const exportType = isQC && !hasExportVliTaiPermission ? 2 : 1;
      const result = await post(`projects/${projectId}/models/${modelId}/export/trigger/${exportType}`);

      const { status } = result;
      setLastStatus(status);
    },
    [projectId, isQC, hasExportVliTaiPermission]
  );

  const saveFile = useDownloadFileApi();
  const downloadAndSaveFile = useCallback(
    async (modelId: ProjectVersion) => {
      setLastStatus(undefined);
      await saveFile.downloadAndSaveFile(`projects/${projectId}/models/${modelId}`, `${projectId}-${modelId}.tai`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, saveFile.downloadAndSaveFile]
  );

  return {
    downloadAndSaveFile,
    triggerExport,
    loading: loading || saveFile.loading,
    lastStatus: lastStatus ?? saveFile.lastStatus,
    getExportResult,
    exportProgress,
  };
}
